import {
  AUTH_LOGIN,
  AUTH_PROFILE,
  AUTH_STORAGE_KEY,
  PROFILE_FIELD,
  type AuthProfileField,
  type ProfileEditPending,
  type ProfileFieldId,
} from '../constants/auth'
import type { ApiAuthTokens, ApiAuthUser } from '../api/types'

export type StoredAuthSession = {
  accessToken: string
  refreshToken: string
  user: ApiAuthUser
}

export function toE164Phone(mobileDigits: string, countryCode = AUTH_LOGIN.countryCode): string {
  const digits = mobileDigits.replace(/\D/g, '')
  const code = countryCode.replace(/\D/g, '')
  if (digits.startsWith(code) && digits.length > 10) {
    return `+${digits}`
  }
  return `+${code}${digits}`
}

export function displayAuthName(user: ApiAuthUser | null): string {
  if (!user) return ''
  return user.name?.trim() || user.email?.trim() || user.phone?.trim() || 'Account'
}

export function profileInitials(user: ApiAuthUser | null): string {
  const source = displayAuthName(user)
  const parts = source.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0]!.charAt(0)}${parts[1]!.charAt(0)}`.toUpperCase()
  }
  return source.slice(0, 2).toUpperCase()
}

export function displayProfileValue(
  value: string | null | undefined,
  empty = AUTH_PROFILE.emptyValue,
): string {
  const trimmed = value?.trim()
  return trimmed || empty
}

function profileField(
  id: ProfileFieldId,
  label: string,
  raw: string | null | undefined,
  editable: boolean,
): AuthProfileField {
  return {
    id,
    label,
    value: displayProfileValue(raw),
    editable,
    hasValue: Boolean(raw?.trim()),
  }
}

export function profileDisplayFields(user: ApiAuthUser | null): AuthProfileField[] {
  return [
    profileField(PROFILE_FIELD.name, AUTH_PROFILE.nameLabel, user?.name, true),
    profileField(PROFILE_FIELD.phone, AUTH_PROFILE.phoneLabel, user?.phone, true),
    profileField(PROFILE_FIELD.email, AUTH_PROFILE.emailLabel, user?.email, false),
  ]
}

export function profilePhoneSubmitLabel(otpSent: boolean, pending: ProfileEditPending): string {
  if (otpSent) {
    return pending === 'otp-verify' ? AUTH_PROFILE.verifying : AUTH_PROFILE.verify
  }
  if (pending === 'phone') {
    return AUTH_PROFILE.saving
  }
  if (pending === 'otp-request') {
    return AUTH_PROFILE.sendingOtp
  }
  return AUTH_PROFILE.save
}

export function onlyDigits(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, maxLength)
}

export function readAuthSession(): StoredAuthSession | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<StoredAuthSession>
    if (
      typeof parsed.accessToken !== 'string' ||
      typeof parsed.refreshToken !== 'string' ||
      !parsed.user ||
      typeof parsed.user !== 'object'
    ) {
      return null
    }
    return parsed as StoredAuthSession
  } catch {
    return null
  }
}

type AuthSessionListener = (session: StoredAuthSession | null) => void

const authSessionListeners = new Set<AuthSessionListener>()

function emitAuthSession(session: StoredAuthSession | null): void {
  for (const listener of authSessionListeners) {
    listener(session)
  }
}

export function subscribeAuthSession(listener: AuthSessionListener): () => void {
  authSessionListeners.add(listener)
  return () => {
    authSessionListeners.delete(listener)
  }
}

export function writeAuthSession(session: StoredAuthSession): void {
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(session))
  emitAuthSession(session)
}

export function clearAuthSession(): void {
  window.localStorage.removeItem(AUTH_STORAGE_KEY)
  emitAuthSession(null)
}

export function sessionFromTokens(tokens: ApiAuthTokens): StoredAuthSession {
  return {
    accessToken: tokens.accessToken,
    refreshToken: tokens.refreshToken,
    user: tokens.user,
  }
}

export function getAccessToken(): string | null {
  return readAuthSession()?.accessToken ?? null
}

export function getRefreshToken(): string | null {
  return readAuthSession()?.refreshToken ?? null
}
